import './style.css';
import {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Trans } from '@lingui/react/macro';
import List from './list';
import { LoadingIndicator } from '@thermon-platforms/thermon-ui-core';
import useSessionStore from 'store/session';
import useDevicesStore from 'store/devices';
import useAlarmsStore from 'store/alarms';
import { getAllChildDevicesByType, getAllCircuits } from 'util/device';
import { useLocation } from 'react-router-dom';
import { getConfigItem } from 'util/device';
import { debounce } from 'lodash';


function DevicesList() {
  const minSidebarWidth = 240;
  const maxSidebarWidth = 700;
  const location = useLocation();
  const [sessionStore, sessionStoreActions] = useSessionStore();
  const [alarmsStore] = useAlarmsStore();
  const [devicesStore] = useDevicesStore();
  const [viewAs, setViewAs] = useState(sessionStore.viewAs || '');
  const [devices, setDevices] = useState();
  const site = devicesStore.devices.find(d => d.type == 'Site');
  const [search, setSearch] = useState('');
  const [allCircuits, setAllCircuits] = useState([]);
  const [width, setWidth] = useState(sessionStore.devicesListWidth || 300);
  const sidebarRef = useRef(null);
  const isResizingRef = useRef(false);
  const currentWidthRef = useRef(sessionStore.devicesListWidth || 300);

  /*
    If the path is /devices/tag-TagName let's default to the tag view
  */
  useEffect(() => {
    const isTagView = location?.pathname?.split('/').slice(2, 999)?.[0]?.startsWith('tag-');
    if (isTagView) setViewAs('Tag');
  }, [location]);

  useEffect(() => {
    if (!site) return;
    setAllCircuits(getAllCircuits(site));
  }, [devicesStore]);

  useEffect(() => {
    setSearch(''); // Clear search on view change
    sessionStoreActions.setViewAs(viewAs);
    // Return panels and their child nodes
    if (viewAs == 'Panel') {
      const panels = getAllChildDevicesByType(site, 'Panel').filter(panel => {
        // Filter out Duo panels
        return panel.deviceModel != 44;
      });
      return setDevices(panels);
    }

    // For tags, let's find all circuits with a given tag and
    // create virtual devices for the tag(s) and their child
    // nodes
    if (viewAs == 'Tag') {
      const circuits = allCircuits;
      const tags = {};
      circuits.forEach(circuit => {
        /*
          NOTE - This is a temporary solution until we have a better tagging system
        */
        const tag = getConfigItem(circuit, 'ProcessTag')?.value;
        if (tag) {
          if (!tags[tag]) tags[tag] = [];
          tags[tag].push(circuit);
        }
        /* / */

        if (circuit.tags?.length) {
          circuit.tags.forEach(tag => {
            if (!tags[tag]) tags[tag] = [];
            tags[tag].push(circuit);
          });
        }
      });

      return setDevices(Object.keys(tags).map(key => {
        return {
          type: 'Tag',
          name: key,
          id: `tag-${encodeURI(key)}`,
          children: tags[key]
        };
      }));
    }

    if (viewAs == 'Alarms') {
      if (!alarmsStore.alarms?.length) return setDevices([]);
      const circuits = allCircuits;
      return setDevices(circuits.filter(circuit => {
        return alarmsStore.alarms.find(alarm => alarm.deviceId == circuit.id);
      }));
    }

    if (viewAs == 'LockoutTagOut') {
      return setDevices(
        [...devicesStore.shallowDevices.values()]
          .filter(({ model }) => getConfigItem(model, viewAs)?.value == 'on')
          .map(({ model }) => model)
      );
    }

    if (viewAs == 'PowerState') {
      const circuits = allCircuits;

      return setDevices(circuits.filter(circuit => {
        return (getConfigItem(circuit, viewAs)?.value || '').toLowerCase().includes('forcedon');
      }));
    }

    if (viewAs == 'Stale') {
      const circuits = allCircuits;

      return setDevices(circuits.filter(circuit => {
        return circuit.dataIsStale;
      }));
    }

    // Fall back to normal returning everything
    return setDevices(devicesStore.devices);
  }, [viewAs, devicesStore, allCircuits]);

  // NOTE - We want to update the view when alarms change but only if we're in the alarms view,
  // otherwise it can be disruptive to re-render when alarms update
  useEffect(() => {
    if (viewAs == 'Alarms') {
      if (!alarmsStore.alarms?.length) return setDevices([]);
      const circuits = allCircuits;
      setDevices(circuits.filter(circuit => {
        return alarmsStore.alarms.find(alarm => alarm.deviceId == circuit.id);
      }));
    }
  }, [alarmsStore, allCircuits]);

  // If the user is searching, let's filter the devices by name
  useEffect(() => {
    if (viewAs && viewAs != '') return; // Only apply search on default view
    if (!search) {
      return setDevices(devicesStore.devices);
    }
    const circuits = allCircuits;
    setDevices(circuits.filter(circuit => {
      return circuit.name.toLowerCase().includes(search.toLowerCase());
    }));
  }, [search, devicesStore, allCircuits]);

  const handleSearchChange = useMemo(() => debounce((value) => setSearch(value), 500), []);

  useEffect(() => {
    currentWidthRef.current = width;
    if (sidebarRef.current) {
      sidebarRef.current.style.width = `${width}px`;
    }
  }, [width]);

  // Handle resizing of the sidebar
  useEffect(() => {
    const handlePointerMove = event => {
      if (!isResizingRef.current) return;
      const maxAllowedWidth = Math.max(minSidebarWidth, Math.min(maxSidebarWidth, window.innerWidth - 320));
      const nextWidth = Math.min(maxAllowedWidth, Math.max(minSidebarWidth, event.clientX));
      if (nextWidth === currentWidthRef.current) return;
      currentWidthRef.current = nextWidth;
      if (sidebarRef.current) {
        sidebarRef.current.style.width = `${nextWidth}px`;
      }
    };

    const stopResizing = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');

      const finalWidth = currentWidthRef.current;
      setWidth(finalWidth);
      sessionStoreActions.setDevicesListWidth(finalWidth);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResizing);
    window.addEventListener('pointercancel', stopResizing);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResizing);
      window.removeEventListener('pointercancel', stopResizing);
      stopResizing();
    };
  }, []);

  return (
    <div ref={sidebarRef} className="sidebar devices-list">
      {!viewAs ? <div className="search">
        <div className="search-input">
          <input
            placeholder="Search circuits..."
            type="text"
            onChange={e => handleSearchChange(e.target.value)}
          />
        </div>
      </div> : null}
      {devicesStore.loading && !devicesStore.updated ? <LoadingIndicator /> :
        devices?.length ? <List devices={devices} showChildren={viewAs != 'LockoutTagOut'} /> :
          <div className="no-devices"><Trans>No devices found</Trans></div>
      }
      <div className="view-options">
        <Trans>View As</Trans>: &nbsp;
        <select value={viewAs} onChange={event => setViewAs(event.target.value)}>
          <option value=""><Trans>Default</Trans></option>
          <option value="Panel"><Trans>Panels</Trans></option>
          <option value="Tag"><Trans>Tags</Trans></option>
          <option value="Alarms"><Trans>Alarms</Trans></option>
          <option value="LockoutTagOut"><Trans>Lockout / Tag Out</Trans></option>
          <option value="PowerState"><Trans>Forced On</Trans></option>
          <option value="Stale"><Trans>Stale Data</Trans></option>
        </select>
      </div>
      <div
        className="drag-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize device list"
        onPointerDown={event => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          event.preventDefault();
          isResizingRef.current = true;
          document.body.style.cursor = 'ew-resize';
          document.body.style.userSelect = 'none';
        }}
      />
    </div>
  );
}

export default DevicesList;
